import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Send, User, Bot, Trash2 } from 'lucide-react';
import { API } from '@/App';
import { toast } from 'sonner';
import confirmToast from '@/utils/confirmToast';

export default function CaseNotesTimeline({ caseId, currentUser }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteContent, setNoteContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [caseId]);

  const fetchNotes = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await fetch(`${API}/crm/notes/case/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setNotes(data);
      }
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim()) {
      toast.error('Note content cannot be empty');
      return;
    }
    
    setSaving(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`${API}/crm/notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          content: noteContent,
          entity_type: 'case',
          entity_id: caseId
        })
      });
      
      if (response.ok) {
        toast.success('Note added');
        setNoteContent('');
        fetchNotes();
      } else {
        toast.error('Failed to add note');
      }
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    confirmToast(
      'This note will be permanently deleted.',
      async () => {
        const token = localStorage.getItem('token');
        
        try {
          const response = await fetch(`${API}/crm/notes/${noteId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (response.ok) {
            toast.success('Note deleted successfully');
            fetchNotes();
          } else {
            const error = await response.json();
            toast.error(error.detail || 'Failed to delete note');
          }
        } catch (error) {
          toast.error('Failed to delete note');
        }
      },
      {
        title: 'Delete Note?',
        confirmText: 'Delete',
        cancelText: 'Cancel'
      }
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-violet-600" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 mb-6">Case Notes</h2>
      
      {/* Add Note Editor */}
      <div className="bg-white rounded-xl border-2 border-violet-200 p-6 mb-8">
        <h3 className="font-semibold text-slate-900 mb-3">Add New Note</h3>
        <Textarea
          value={noteContent}
          onChange={(e) => setNoteContent(e.target.value)}
          placeholder="Type your note here..."
          className="min-h-[150px] mb-3"
        />
        <Button
          onClick={handleAddNote}
          disabled={saving || !noteContent.trim()}
          className="bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Adding...</>
          ) : (
            <><Send className="w-4 h-4 mr-2" /> Add Note</>
          )}
        </Button>
      </div>

      {/* Notes Timeline */}
      <div className="space-y-4">
        <h3 className="font-semibold text-slate-700 mb-4">Timeline ({notes.length} notes)</h3>
        
        {notes.length === 0 ? (
          <div className="bg-slate-50 rounded-xl border border-slate-200 p-12 text-center">
            <p className="text-slate-500">No notes yet. Add the first note above.</p>
          </div>
        ) : (
          notes.map((note) => (
            <div
              key={note.note_id}
              className={`relative pl-8 pb-8 ${
                note.is_system ? 'ml-8' : ''
              }`}
            >
              {/* Timeline line */}
              <div className="absolute left-3 top-6 bottom-0 w-0.5 bg-slate-200"></div>
              
              {/* Timeline dot */}
              <div className={`absolute left-0 top-0 w-6 h-6 rounded-full border-4 border-white ${
                note.is_system 
                  ? 'bg-blue-500' 
                  : 'bg-violet-600'
              } shadow-md flex items-center justify-center`}>
                {note.is_system ? (
                  <Bot className="w-3 h-3 text-white" />
                ) : (
                  <User className="w-3 h-3 text-white" />
                )}
              </div>
              
              {/* Note Card */}
              <div className={`ml-6 rounded-xl border-2 p-5 group ${
                note.is_system
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-white border-slate-200 hover:border-violet-200 transition-colors'
              }`}>
                {/* Note Content - Primary focus */}
                <div className="text-slate-700 whitespace-pre-wrap mb-3">
                  {note.content}
                </div>
                
                {/* Footer: Author, timestamp, and delete button */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-200">
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    <div className="flex items-center gap-2">
                      {note.is_system ? (
                        <>
                          <Bot className="w-3 h-3" />
                          <span className="font-medium">System</span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-600 text-white text-[10px] font-bold">
                            AUTO
                          </span>
                        </>
                      ) : (
                        <>
                          <User className="w-3 h-3" />
                          <span className="font-medium text-slate-700">{currentUser?.name || 'User'}</span>
                        </>
                      )}
                    </div>
                    <span className="text-slate-400">•</span>
                    <span className="text-slate-500">
                      {new Date(note.created_at).toLocaleString()}
                    </span>
                  </div>
                  
                  {/* Delete button - in footer */}
                  {!note.is_system && note.created_by === currentUser?.user_id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNote(note.note_id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity rounded-lg h-6 px-2 text-red-500 hover:text-red-600 hover:bg-red-50"
                      title="Delete note"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
